import { useEffect, useRef, useState } from 'react';
import { FaPause, FaPlay, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

type Props = {
    src: string;
    title?: string;
    className?: string;
    preload?: 'none' | 'metadata' | 'auto';
};

function fmtTime(t: number): string {
    if (!isFinite(t)) return '0:00';
    const s = Math.max(0, Math.floor(t));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function MiniAudioPlayer({
    src,
    title = 'Audio',
    className = '',
    preload = 'metadata',
}: Props): JSX.Element {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [current, setCurrent] = useState(0);
    const [volume, setVolume] = useState(1);
    const [prevVolume, setPrevVolume] = useState(1);

    useEffect(() => {
        const el = audioRef.current;
        if (el === null) return;

        const onLoaded = (): void => {
            setDuration(el.duration === 0 ? 0 : el.duration);
            setReady(true);
        };
        const onTime = (): void => { setCurrent(el.currentTime === 0 ? 0 : el.currentTime); };
        const onPlay = (): void => { setPlaying(true); };
        const onPause = (): void => { setPlaying(false); };
        const onEnded = (): void => { setPlaying(false); };

        el.addEventListener('loadedmetadata', onLoaded);
        el.addEventListener('timeupdate', onTime);
        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        el.addEventListener('ended', onEnded);
        return () => {
            el.removeEventListener('loadedmetadata', onLoaded);
            el.removeEventListener('timeupdate', onTime);
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('ended', onEnded);
        };
    }, []);

    const toggle = (): void => {
        const el = audioRef.current;
        if (el === null) return;
        if (el.paused) {
            el.play().catch(() => { }); //
        } else {
            el.pause();
        }
    };

    const onSeek = (v: number): void => {
        const el = audioRef.current;
        if (el === null) return;
        el.currentTime = Math.min(duration, Math.max(0, v));
    };

    const onVolume = (v: number): void => {
        const el = audioRef.current;
        if (el === null) return;
        const nv = Math.min(1, Math.max(0, v));
        el.volume = nv;
        setVolume(nv);
    };

    return (
        <div
            className={`w-full rounded-xl bg-gray-100 backdrop-blur p-4 shadow-sm ${className}`}
            role='region'
            aria-label={`Audio player: ${title}`}
        >
            <audio ref={audioRef} src={src} preload={preload} />

            <div className='flex items-center gap-3'>
                <button
                    onClick={toggle}
                    className='px-3 py-2 rounded-lg text-sm font-medium hover:shadow'
                    aria-pressed={playing}
                    aria-label={playing ? 'Pause' : 'Play'}
                >
                    {playing ? <FaPause /> : <FaPlay />}
                </button>

                {/* Seek */}
                <div className='flex-1 flex items-center gap-2'>
                    <span className='text-xs tabular-nums text-black/90 w-12 text-right'>
                        {fmtTime(current)}
                    </span>
                    <input
                        type='range'
                        min={0}
                        max={duration === 0 ? 0 : duration}
                        step={0.01}
                        value={current}
                        onChange={(e) => { onSeek(parseFloat(e.target.value)); }}
                        className='w-full'
                        aria-label='Seek'
                        disabled={!ready}
                    />
                    <span className='text-xs tabular-nums text-black/90 w-12'>
                        {fmtTime(duration)}
                    </span>
                </div>

                {/* Volume */}
                <div className='flex items-center gap-2'>
                    <div onClick={() => { setPrevVolume(volume); onVolume(volume === 0 ? prevVolume : 0); }} className='cursor-pointer'>
                        {volume === 0 ? (
                            <FaVolumeMute className='text-black/90' />
                        ) : (
                            <FaVolumeUp className='text-black/90' />
                        )}
                    </div>
                    <input
                        type='range'
                        min={0}
                        max={1}
                        step={0.01}
                        value={volume}
                        onChange={(e) => { onVolume(parseFloat(e.target.value)); }}
                        className='w-24'
                        aria-label='Volume'
                    />
                </div>
            </div>
        </div>
    );
}
